import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Checkbox,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Box,
    Divider,
    TextField,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';

// Standaard categorie volgorde
const CATEGORIES = [
    'Cameras', 'Drones', 'Lens', 'Monitors', 'Statief', 'Geluid', 'Grip', 'Licht',
    'Camera Accessoires', 'Special effects', 'Studio equipment', 'Vervoer en karren'
];

const CopyMaterialsDialog = ({ 
    open, 
    onClose, 
    currentDraaidag,
    onCopy,
    reservations = []
}) => {
    const [selectedDraaidag, setSelectedDraaidag] = useState(null);
    const [selectedMaterials, setSelectedMaterials] = useState([]);
    const [availabilityMap, setAvailabilityMap] = useState({});
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [draaidagMaterials, setDraaidagMaterials] = useState({});
    const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    // Filter draaidagen die materialen hebben
    const draaidagenWithMaterials = useMemo(() => {
        // Groepeer reservations per draaidag
        const draaidagMap = {};
        
        reservations.forEach(reservation => {
            if (reservation.fields?.IDdraaidagOutlook?.[0] && reservation.fields?.Materiaal && Array.isArray(reservation.fields.Materiaal) && reservation.fields.Materiaal.length > 0) {
                const draaidagId = reservation.fields.IDdraaidagOutlook[0];
                
                if (!draaidagMap[draaidagId]) {
                    draaidagMap[draaidagId] = {
                        id: draaidagId,
                        naam: reservation.fields.DraaidagNaamOutlook?.[0] || reservation.fields.DraaidagNaam?.[0] || 'Onbekende draaidag',
                        datum: reservation.fields.Start,
                        materiaalIds: new Set()
                    };
                }
                
                // Voeg materiaal IDs toe
                reservation.fields.Materiaal.forEach(matId => {
                    draaidagMap[draaidagId].materiaalIds.add(matId);
                });
            }
        });
        
        // Converteer naar array en filter huidige draaidag eruit
        return Object.values(draaidagMap)
            .filter(d => d.id !== currentDraaidag?.id)
            .sort((a, b) => new Date(b.datum) - new Date(a.datum)); // Sorteer op datum (nieuwste eerst)
    }, [reservations, currentDraaidag]);

    // Filter draaidagen op zoekterm
    const filteredDraaidagen = useMemo(() => {
        if (!searchTerm) return draaidagenWithMaterials;
        
        const lowerSearch = searchTerm.toLowerCase();
        return draaidagenWithMaterials.filter(d => 
            d.naam.toLowerCase().includes(lowerSearch) ||
            d.datum.includes(searchTerm)
        );
    }, [draaidagenWithMaterials, searchTerm]);

    // Groepeer materialen per categorie
    const materialsByCategory = useMemo(() => {
        const grouped = {};
        
        // Initialiseer alle categorieën
        CATEGORIES.forEach(cat => {
            grouped[cat] = [];
        });
        
        // Groepeer materialen
        if (selectedDraaidag) {
            Array.from(selectedDraaidag.materiaalIds).forEach(materialId => {
                const material = draaidagMaterials[materialId];
                if (material && material.fields.Categorie) {
                    const category = material.fields.Categorie;
                    if (!grouped[category]) {
                        grouped[category] = [];
                    }
                    grouped[category].push({ id: materialId, material });
                }
            });
        }
        
        // Filter lege categorieën
        return Object.entries(grouped)
            .filter(([_, materials]) => materials.length > 0)
            .reduce((acc, [cat, materials]) => {
                acc[cat] = materials;
                return acc;
            }, {});
    }, [selectedDraaidag, draaidagMaterials]);

    // Toggle categorie accordion
    const handleToggleCategory = (category) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    // Check beschikbaarheid van materialen (gebruik dezelfde logica als SimpleMaterialWizard)
    function checkAvailability(materialIds, materials = null) {
        if (!currentDraaidag || materialIds.length === 0) return;
        setIsCheckingAvailability(true);
        try {
            const availability = {};
            const draaidagStart = new Date(currentDraaidag.fields.StartDatum || currentDraaidag.fields.Start);
            const draaidagEnd = new Date(currentDraaidag.fields.EindDatum || currentDraaidag.fields.Eind);
            
            // Gebruik dezelfde logica als SimpleMaterialWizard
            materialIds.forEach(materialId => {
                const materialsToCheck = materials || draaidagMaterials;
                const material = materialsToCheck[materialId];
                const materialReservations = processReservationsData[materialId] || [];
                const hasConflict = materialReservations.some(reservation => {
                    if (reservation.reservationId === currentDraaidag.currentReservationId) return false;
                    const resStart = new Date(reservation.startDate);
                    const resEnd = new Date(reservation.endDate);
                    return (resStart <= draaidagEnd && resEnd >= draaidagStart);
                });
                availability[materialId] = !hasConflict;
            });
            setAvailabilityMap(availability);
            const availableMaterials = materialIds.filter(id => availability[id]);
            setSelectedMaterials(availableMaterials);
        } finally {
            setIsCheckingAvailability(false);
        }
    }

    // Laad materialen voor geselecteerde draaidag
    useEffect(() => {
        if (!selectedDraaidag || !open) return;
        
        const loadMaterials = async () => {
            setIsLoadingMaterials(true);
            try {
                // Haal materiaal details op voor de geselecteerde draaidag via backend
                const materialIds = Array.from(selectedDraaidag.materiaalIds);
                if (materialIds.length === 0) {
                    setDraaidagMaterials({});
                    return;
                }
                
                // Haal materialen op via backend (security: API key niet in frontend)
                const response = await axios.get(
                    `${process.env.REACT_APP_CURRENT_URL}/Api/materialen/batch`,
                    { params: { ids: materialIds.join(',') } }
                );
                
                // Maak een map van materiaal ID naar materiaal object
                const materials = {};
                response.data.forEach(record => {
                    materials[record.id] = record;
                });
                
                setDraaidagMaterials(materials);
                
                // Check beschikbaarheid direct na laden, geef materials mee
                await checkAvailability(materialIds, materials);
                
                // Open alle categorieën standaard
                setExpandedCategories(new Set(CATEGORIES));
                
            } catch (error) {
                console.error('Error loading materials:', error);
            } finally {
                setIsLoadingMaterials(false);
            }
        };
        
        loadMaterials();
    }, [selectedDraaidag, open, currentDraaidag]);

    // Process reservations data voor beschikbaarheidscheck (zelfde logica als SimpleMaterialWizard)
    const processReservationsData = useMemo(() => {
        const reservationsData = {};

        if (Array.isArray(reservations)) {
            reservations.forEach(record => {
                if (record.fields && record.fields.Materiaal && Array.isArray(record.fields.Materiaal)) {
                    // Get the date range for this reservation
                    const startDate = new Date(record.fields.Start);
                    const endDate = new Date(record.fields.Eind);
                    const draaidagNaam = record.fields.DraaidagNaam?.[0] || record.fields.DraaidagNaamOutlook?.[0] || 'Onbekende draaidag';
                    
                    record.fields.Materiaal.forEach(materialId => {
                        if (!reservationsData[materialId]) {
                            reservationsData[materialId] = [];
                        }
                        
                        // Store reservation info with draaidag name
                        reservationsData[materialId].push({
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString(),
                            draaidagNaam: draaidagNaam,
                            reservationId: record.id
                        });
                    });
                }
            });
        }

        return reservationsData;
    }, [reservations]);

    const handleToggleMaterial = (materialId) => {
        setSelectedMaterials(prev => {
            if (prev.includes(materialId)) {
                return prev.filter(id => id !== materialId);
            } else {
                return [...prev, materialId];
            }
        });
    };

    const handleCopy = () => {
        // Geef de volledige materiaal objecten door
        const materialsToSave = selectedMaterials
            .map(id => draaidagMaterials[id])
            .filter(Boolean);
        
        onCopy(materialsToSave);
        onClose();
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Kopieer materialen van een andere draaidag
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Zoek op naam of datum..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                    />
                </Box>

                {!selectedDraaidag ? (
                    <>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Selecteer een draaidag om materialen van te kopiëren:
                        </Typography>
                        <List>
                            {filteredDraaidagen.length === 0 ? (
                                <ListItem>
                                    <ListItemText 
                                        primary="Geen draaidagen gevonden met materialen"
                                        secondary={searchTerm ? "Probeer een andere zoekterm" : null}
                                    />
                                </ListItem>
                            ) : (
                                filteredDraaidagen.map((draaidag) => (
                                    <ListItem 
                                        key={draaidag.id}
                                        button
                                        onClick={() => setSelectedDraaidag(draaidag)}
                                    >
                                        <ListItemText
                                            primary={draaidag.naam}
                                            secondary={`${formatDate(draaidag.datum)} - ${draaidag.materiaalIds.size} materialen`}
                                        />
                                    </ListItem>
                                ))
                            )}
                        </List>
                    </>
                ) : (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Button 
                                size="small" 
                                onClick={() => {
                                    setSelectedDraaidag(null);
                                    setSelectedMaterials([]);
                                    setDraaidagMaterials({});
                                }}
                            >
                                ← Terug naar draaidagen
                            </Button>
                        </Box>
                        
                        <Typography variant="h6" gutterBottom>
                            {selectedDraaidag.naam}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            {formatDate(selectedDraaidag.datum)}
                        </Typography>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        {isLoadingMaterials || isCheckingAvailability ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <CircularProgress />
                                <Typography variant="body2" sx={{ mt: 2 }}>
                                    {isLoadingMaterials ? 'Materialen laden...' : 'Beschikbaarheid controleren...'}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {Object.keys(availabilityMap).length > 0 && (
                                    <>
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            {Object.values(availabilityMap).filter(v => v).length} van {Object.keys(availabilityMap).length} materialen beschikbaar
                                        </Alert>
                                        
                                        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                            <Button 
                                                size="small" 
                                                variant="outlined"
                                                onClick={() => {
                                                    // Selecteer alle beschikbare materialen
                                                    const availableMaterials = Array.from(selectedDraaidag.materiaalIds)
                                                        .filter(id => availabilityMap[id] !== false);
                                                    setSelectedMaterials(availableMaterials);
                                                }}
                                            >
                                                Selecteer alles
                                            </Button>
                                            <Button 
                                                size="small" 
                                                variant="outlined"
                                                onClick={() => setSelectedMaterials([])}
                                            >
                                                Deselecteer alles
                                            </Button>
                                        </Box>
                                    </>
                                )}
                                
                                {/* Materialen gegroepeerd per categorie */}
                                {Object.entries(materialsByCategory).map(([category, materials]) => (
                                    <Accordion 
                                        key={category}
                                        expanded={expandedCategories.has(category)}
                                        onChange={() => handleToggleCategory(category)}
                                        sx={{ mb: 1 }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography>{category} ({materials.length})</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <List dense>
                                                {materials.map(({ id: materialId, material }) => {
                                                    const isAvailable = availabilityMap[materialId] !== false;
                                                    
                                                    return (
                                                        <ListItem 
                                                            key={materialId}
                                                            dense
                                                            disabled={!isAvailable}
                                                        >
                                                            <Checkbox
                                                                checked={selectedMaterials.includes(materialId)}
                                                                onChange={() => handleToggleMaterial(materialId)}
                                                                disabled={!isAvailable}
                                                            />
                                                            <ListItemText
                                                                primary={material.fields.ID}
                                                                secondary={material.fields.Omschrijving}
                                                            />
                                                            {!isAvailable && (
                                                                <Chip 
                                                                    label="Niet beschikbaar" 
                                                                    size="small" 
                                                                    color="error"
                                                                />
                                                            )}
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    Annuleren
                </Button>
                {selectedDraaidag && (
                    <Button 
                        onClick={handleCopy}
                        variant="contained"
                        disabled={selectedMaterials.length === 0 || isLoadingMaterials || isCheckingAvailability}
                    >
                        Kopieer {selectedMaterials.length} materialen
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CopyMaterialsDialog; 